// tcms-frontend/src/pages/DiagnosticsNew.js


import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import axios from "axios";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { security_key, BASE_URL } from "../global";
import "./Student.css";

// ----- helpers for base url handling -----
const RAW_BASE = (process.env.REACT_APP_DIAGNOSTICS_API_URL).replace(/\/+$/, "");
const withApi = (b) => (b.endsWith("/api") ? b : `${b}/api`);
const withoutApi = (b) => b.replace(/\/api$/i, "");
const CANDIDATE_BASES = Array.from(new Set([RAW_BASE, withApi(RAW_BASE), withApi(withoutApi(RAW_BASE))]));

// optional partner auth header
const partnerHeaders = {};
if (process.env.REACT_APP_PARTNER_KEY) {
    partnerHeaders["X-Partner-Key"] = process.env.REACT_APP_PARTNER_KEY;
}

// normalize arrays from various shapes
const asArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.rows)) return payload.rows;
    return [];
};

// try GET across candidate bases; if all fail, try POST (for flexibility)
async function fetchAcrossBases(pathTemplate /* e.g. /partner/users/by-centre/${fk} */, paramsOrBody) {
    // GET
    for (const base of CANDIDATE_BASES) {
        const url = `${base}${pathTemplate}`;
        try {
            const r = await axios.get(url, { params: paramsOrBody, headers: partnerHeaders });
            if (r?.status >= 200 && r?.status < 300) return { ok: true, data: r.data, via: `GET ${url}` };
        } catch {}
    }
    // POST fallback
    for (const base of CANDIDATE_BASES) {
        const url = `${base}${pathTemplate}`;
        try {
            const r = await axios.post(url, paramsOrBody, { headers: partnerHeaders });
            if (r?.status >= 200 && r?.status < 300) return { ok: true, data: r.data, via: `POST ${url}` };
        } catch {}
    }
    return { ok: false, error: `No route responded for ${pathTemplate}` };
}

const DIAG_APP = (process.env.REACT_APP_DIAGNOSTICS_APP_URL || "http://localhost:5173").replace(/\/+$/, "");
const ASSIGN_PATH = process.env.REACT_APP_DIAGNOSTICS_ASSIGN_PATH || "/diagnostics/assign";
const START_PATH  = process.env.REACT_APP_DIAGNOSTICS_START_PATH  || "/diagnostics/welcome";

function DiagnosticsNew() {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [ylMap, setYlMap] = useState({});
    const [pendingKeys, setPendingKeys] = useState(new Set());

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const fk = sessionStorage.getItem("key");
                const countryId = sessionStorage.getItem("country_code") || null;
                if (!fk) {
                    toast.error("Missing centre key in sessionStorage.");
                    setLoading(false);
                    return;
                }

                // 1) Diagnostics users
                const usersAttempt = await fetchAcrossBases(`/partner/users/by-centre/${encodeURIComponent(fk)}`, {});
                if (!usersAttempt.ok) {
                    toast.error("Could not load diagnostics users. " + usersAttempt.error);
                } else {
                    console.debug("[Diagnostics] users via:", usersAttempt.via);
                }
                const rawUsers = asArray(usersAttempt.data);
                const normUsers = rawUsers.map((r, idx) => {
                    const id = r.user_id ?? r.id ?? idx;
                    const user_key = r.user_key ?? r.key ?? r.userKey ?? null;
                    const registered_level_id = r.registered_level_id ?? r.reg_level_id ?? r.registeredLevelId ?? null;
                    const user_year_level = r.user_year_level ?? r.year_level_id ?? r.userYearLevel ?? null;
                    return {
                        ...r,
                        id: user_key || id,
                        user_key,
                        user_name: r.user_name ?? r.name ?? r.full_name ?? "-",
                        registered_level_id,
                        user_year_level,
                    };
                });
                setUsers(normUsers);

                // 2) Pending assignments
                const pendingAttempt = await fetchAcrossBases(`/partner/assignments/pending/${encodeURIComponent(fk)}`, {});
                if (!pendingAttempt.ok) {
                    console.warn("[Diagnostics] pending not available:", pendingAttempt.error);
                } else {
                    console.debug("[Diagnostics] pending via:", pendingAttempt.via);
                }
                const rawPending = asArray(pendingAttempt.data);
                setPendingKeys(new Set(rawPending.map((row) => row.user_key ?? row.userKey ?? row.key).filter(Boolean)));

                // 3) Pretty year-level names from TCMS
                try {
                    const ylRes = await axios.post(BASE_URL + "API/api_index.php", {
                        security_key,
                        mode: "listYearLevels",
                        country_id: countryId,
                    });
                    const ylRows = asArray(ylRes?.data);
                    const m = {};
                    ylRows.forEach((r) => {
                        const id = r.level_id ?? r.activity_level_id ?? r.id;
                        const name = r.level_name ?? r.name;
                        if (id != null && name) m[id] = name;
                    });
                    setYlMap(m);
                } catch {
                    /* non-fatal */
                }
            } catch (e) {
                console.error(e);
                toast.error("Failed to load diagnostics data.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const openWithToken = async (row, purpose /* 'assign' | 'start' */) => {
        try {
            if (!row?.user_key) {
                toast.error("Missing user_key for token.");
                return;
            }
            const fk = sessionStorage.getItem("key");
            if (!fk) {
                toast.error("Missing centre key in sessionStorage.");
                return;
            }
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key,
                mode: "diagnostics_generate_token",
                fk_tenat_key: fk,
                user_key: row.user_key,
                purpose,
            });
            const ok = res?.data?.success;
            const token = res?.data?.token ?? res?.data?.data?.token;
            if (ok && token) {
                const path = purpose === "assign" ? ASSIGN_PATH : START_PATH;
                const url = `${DIAG_APP}${path}?token=${encodeURIComponent(token)}`;
                window.open(url, "_blank", "noopener,noreferrer");
            } else {
                toast.error(res?.data?.message || res?.data?.error || "Could not initialize token.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Could not initialize token.");
        }
    };

    const handleAssign  = (row) => openWithToken(row, "assign");
    const handleAttempt = (row) => openWithToken(row, "start");

    const columns = useMemo(
        () => [
            {
                headerName: "Name",
                field: "user_name",
                headerClassName: "super-app-theme--header",
                flex: 1,
                valueGetter: (params) => params.row?.user_name || "-",
            },
            {
                headerName: "Registered Level",
                field: "registered_year_level",
                headerClassName: "super-app-theme--header",
                flex: 1,
                valueGetter: (params) => {
                    const id = params.row?.registered_year_level;
                    return ylMap[id] || (id ? `#${id}` : "-");
                },
            },
            {
                headerName: "User Year Level",
                field: "user_year_level",
                headerClassName: "super-app-theme--header",
                flex: 1,
                valueGetter: (params) => {
                    const id = params.row?.user_year_level;
                    return ylMap[id] || (id ? `#${id}` : "-");
                },
            },
            {
                headerName: "User Key",
                field: "user_key",
                headerClassName: "super-app-theme--header",
                flex: 1,
                valueGetter: (params) => params.row?.user_key || "-",
            },
            {
                headerName: "Actions",
                field: "actions",
                headerClassName: "super-app-theme--header",
                sortable: false,
                filterable: false,
                width: 220,
                renderCell: (params) => {
                    const key = params.row?.user_key || "";
                    const hasPending = key && pendingKeys.has(key);
                    return hasPending ? (
                        <Button
                            variant="contained"
                            size="small"
                            className="primary-filled"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAttempt(params.row);
                            }}
                        >
                            Attempt test
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            size="small"
                            className="primary-filled"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAssign(params.row);
                            }}
                        >
                            Assign Test
                        </Button>
                    );
                },
            },
        ],
        [ylMap, pendingKeys]
    );

    return (
        <>
            <ToastContainer position="top-center" />
            <div className="page-wrapper">
                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="page-title"> Initial Diagnostics (New) </h5>
                        <div role="presentation">
                            <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                                <NavLink underline="hover" color="inherit" to="/calendar">
                                    Home
                                </NavLink>
                                <Typography color="text.primary">Initial Diagnostics (New)</Typography>
                            </Breadcrumbs>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <NavLink className="primary-filled rounded-10 p-2" to="/AddInitialDiagnosticsNew">
                            <AddIcon className="btn-icon" /> New Initial Diagnostics
                        </NavLink>
                    </div>
                </div>

                <Card className="my-card p-1 bg-white">
                    <Card.Body>
                        <Row>
                            <Col sm={12}>
                                <Box
                                    sx={{
                                        height: "fit-content",
                                        width: "100%",
                                        "& .super-app-theme--header": { backgroundColor: "#f4f4f4" },
                                    }}
                                >
                                    <DataGrid
                                        sx={{
                                            ".MuiDataGrid-cell:focus": { outline: "none" },
                                            "& .MuiDataGrid-row:hover": { cursor: "pointer" },
                                        }}
                                        autoHeight
                                        loading={loading}
                                        slots={{ loadingOverlay: LinearProgress, toolbar: GridToolbar }}
                                        slotProps={{
                                            toolbar: {
                                                csvOptions: { fileName: "DiagnosticsUsers" },
                                                printOptions: {
                                                    pageStyle: `@media print { @page { size: A4 landscape; margin: 0; } }`,
                                                    disableToolbarButton: true,
                                                    hideFooter: true,
                                                    hideToolbar: true,
                                                    fileName: "DiagnosticsUsers",
                                                    orientation: "landscape",
                                                },
                                                users,
                                                showQuickFilter: true,
                                            },
                                        }}
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 10 } },
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        rows={users}
                                        columns={columns}
                                        rowHeight={60}
                                        density="compact"
                                        disableRowSelectionOnClick
                                        showCellVerticalBorder
                                        showColumnVerticalBorder
                                    />
                                </Box>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </div>
        </>
    );
}

export default DiagnosticsNew;
